import { PropsWithChildren, createContext, useContext } from "react";

import { ModelListItemFragment, useModelQuery } from "@/apollo/types";

import { DraftModel, useDraft } from "./QueryEditor/useModelDraft";

export const ModelContext = createContext<{
  id: string;
  isUnsavedDraft: boolean;
  model: ModelListItemFragment | undefined;
  unsavedDraft: DraftModel | undefined;
  error: Error | undefined;
  loading: boolean;
} | null>(null);

export function useModelContext() {
  const ctx = useContext(ModelContext);
  if (!ctx) {
    throw new Error("useModelContext must be used within a ModelProvider");
  }
  return ctx;
}

export function useCurrentModelId() {
  const ctx = useModelContext();
  return ctx.id;
}

export function useCurrentModel() {
  const ctx = useModelContext();
  return ctx.model;
}

export function useCurrentDraftModel() {
  const ctx = useModelContext();
  return ctx.unsavedDraft;
}

export function ModelProvider(
  props: PropsWithChildren<{
    modelId: string;
    isUnsavedDraft?: boolean;
  }>,
) {
  const { data, loading, error } = useModelQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      modelId: props.modelId,
    },
    skip: props.isUnsavedDraft,
  });

  const draft = useDraft(props.modelId, {
    skip: !props.isUnsavedDraft,
  });

  const ctx = {
    id: props.modelId,
    isUnsavedDraft: !!props.isUnsavedDraft,
    model: data?.model,
    unsavedDraft: draft,
    loading,
    error,
  };

  return (
    <ModelContext.Provider value={ctx}>{props.children}</ModelContext.Provider>
  );
}
